<template>
    <div class="col-sm-12 editor-form">
        <h4 class="form-header">
            {{ $t('contracts.basic_info') }}
        </h4>
        <div class="row">
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.customer') }}
                </span>
                <span>
                    <strong>
                        {{ contract.customer.name }}
                    </strong>
                </span>
            </div>
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.contractor') }}
                </span>
                <span>
                    <strong>
                        {{ contract.contractor.name }}
                    </strong>
                </span>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.name') }}
                </span>
                <input
                    id="contractname"
                    v-model="contract.name"
                    type="text"
                    required
                    class="form-control form-control-sm"
                >
            </div>
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.number') }}
                </span>
                <input
                    id="number"
                    v-model="contract.contract_number"
                    type="text"
                    class="form-control form-control-sm"
                >
                <div
                    v-if="contract.id > 0 && hasContractSystem(user)"
                    class="col-12 mt-1 text-end"
                >
                    <button
                        class="btn btn-sm btn-outline-primary form-button"
                        :disabled="!contract.contract_number"
                        @click.stop="showFetchImportedDataModal = true"
                    >
                        {{ $t('contracts.fetch_imported_contract_data') }}
                    </button>
                    <button
                        class="btn btn-sm btn-outline-info form-button"
                        :disabled="!contract.contract_number"
                        @click.stop="showUpdatePriceModal = !showUpdatePriceModal"
                    >
                        {{ $t('contracts.update_contract_prices') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.start') }}
                </span>
                <input
                    id="start"
                    type="date"
                    required
                    :value="timestampToDateInput(contract.start_date)"
                    class="form-control form-control-sm"
                    @blur="updateStartDate"
                >
            </div>
            <div class="col-sm-6 nopads">
                <span class="span-title">
                    {{ $t('contracts.end') }}
                </span>
                <input
                    id="end"
                    type="date"
                    required
                    :value="timestampToDateInput(contract.end_date)"
                    class="form-control form-control-sm"
                    @blur="updateEndDate"
                >
            </div>
        </div>
        <div class="row">
            <div class="col-sm-6">
                <span class="span-title">
                    {{ $t('contracts.worksite_key') }}
                </span>
                <input
                    id="work_site_key"
                    v-model="contract.work_site_key"
                    type="text"
                    class="form-control form-control-sm"
                >
            </div>
            <div class="col-sm-6">
                <!-- Allowed Users Multiselect -->
                <div class="col-sm-12 nopads pr-1">
                    <span class="span-title">
                        {{ $t('contracts.owners') }}
                    </span>
                    <multiselect
                        v-model="allowedUsers"
                        class="multiselect_routa"
                        :placeholder="$t('work_time.select_person')"
                        :deselect-label="$t('common.remove_selection')"
                        :select-label="$t('common.select')"
                        :close-on-select="false"
                        :multiple="true"
                        label="first_name"
                        track-by="id"
                        :searchable="true"
                        :internal-search="true"
                        :disabled="users.length <= 0"
                        :selected-label="$t('common.selected')"
                        :options="users"
                    >
                        <template #singleLabel="props">
                            <span class="option__desc">
                                <span class="option__title">{{ props.option.last_name + ', ' + props.option.first_name }}</span>
                            </span>
                        </template>
                        <template #option="props">
                            <div class="option__desc">
                                <span class="option__title multiselect_detail_block">{{ props.option.last_name + ', ' + props.option.first_name }}</span>
                            </div>
                        </template>
                        <span slot="noResult">{{ $t('common.not_found') }}</span>
                        <span slot="noOptions">{{ $t('common.no_results') }}</span>
                    </multiselect>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-4">
                <span class="span-title">
                    {{ $t('contracts.contract_class.title') }}
                </span>
                <multiselect
                    v-model="contractClass"
                    class="multiselect_routa"
                    :placeholder="$t('street_lights.select_class')"
                    :deselect-label="$t('common.remove_selection')"
                    :select-label="$t('common.select')"
                    :close-on-select="true"
                    :multiple="false"
                    label="name"
                    :class="{required_field : !contractClass}"
                    track-by="id"
                    :searchable="true"
                    :internal-search="true"
                    :disabled="isContractClassLocked"
                    :selected-label="$t('common.selected')"
                    :options="contractClassOptions"
                >
                    <template #singleLabel="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                        </span>
                    </template>
                    <template #option="props">
                        <div class="option__desc">
                            <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                        </div>
                    </template>
                    <span slot="noResult">{{ $t('common.not_found') }}</span>
                    <span slot="noOptions">{{ $t('common.no_results') }}</span>
                </multiselect>
            </div>
            <div class="col-sm-4">
                <span class="span-title">
                    {{ $t('contracts.code') }}
                </span>
                <input
                    id="code"
                    v-model="contract.code"
                    type="text"
                    :disabled="!isInvestment"
                    :required="isInvestment"
                    class="form-control form-control-sm"
                >
            </div>
            <div class="col-sm-4">
                <span class="span-title">
                    {{ $t('work_time.cost_center') }}
                </span>
                <multiselect
                    v-model="contract.cost_center"
                    class="multiselect_routa"
                    :placeholder="$t('street_lights.select_class')"
                    :deselect-label="$t('common.remove_selection')"
                    :select-label="$t('common.select')"
                    :close-on-select="true"
                    :multiple="false"
                    :custom-label="value => getCostCenterString(value)"
                    :class="{required_field : !isInvestment && contractClass && !contract.cost_center}"
                    :disabled="isInvestment || !contractClass"
                    track-by="id"
                    :searchable="true"
                    :internal-search="true"
                    :selected-label="$t('common.selected')"
                    :options="costCenters"
                >
                    <template #singleLabel="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.number }} - {{ props.option.name }}</span>
                        </span>
                    </template>
                    <template #option="props">
                        <div class="option__desc">
                            <span class="option__title multiselect_detail_block">{{ props.option.number }} - {{ props.option.name }}</span>
                        </div>
                    </template>
                    <span slot="noResult">{{ $t('common.not_found') }}</span>
                    <span slot="noOptions">{{ $t('common.no_results') }}</span>
                </multiselect>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-4 nopads">
                <span class="span-title">{{ $t('contracts.contract_collection.title') }}</span>
                <select
                    class="form-select form-select-sm title"
                    v-model="contractCollection"
                >
                    <option :value="null">
                        {{ $t('contracts.select_contract_collection') }}
                    </option>
                    <option
                        v-for="option in contractCollectionOptions"
                        :key="option.id"
                        :value="option.id"
                    >
                        {{ option.name }}
                    </option>
                </select>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-6">
                <span class="span-title">{{ $t('contracts.observation_link') }}</span>
                <span class="tip-light">{{ $t('contracts.observation_link_hint') }}</span>
                <input
                    id="preventObservationOrderLink"
                    v-model="contract.prevent_observation_order_link"
                    type="checkbox"
                    class="form-check-input pt-2"
                    style="margin-right: .5em"
                >
                <label
                    class="form-check-label"
                    for="preventObservationOrderLink"
                >
                    {{ $t('contracts.observation_order_link_prevented') }}
                </label>
            </div>
            <div class="col-sm-6">
                <span class="span-title">{{ $t('contracts.location_sharing_mode') }}</span>
                <label
                    class="pt-2"
                    style="display: block; font-size: .9em"
                >
                    <input
                        v-model="contract.location_sharing_mode"
                        class="me-2"
                        type="radio"
                        :value="1"
                    >
                    {{ $t('contracts.location_sharing_mode_own') }}
                </label>

                <label
                    class="pt-2"
                    style="display: block; font-size: .9em"
                >
                    <input
                        v-model="contract.location_sharing_mode"
                        class="me-2"
                        type="radio"
                        :value="2"
                    >
                    {{ $t('contracts.location_sharing_mode_order') }}
                </label>

                <label
                    class="pt-2"
                    style="display: block; font-size: .9em"
                >
                    <input
                        v-model="contract.location_sharing_mode"
                        class="me-2"
                        type="radio"
                        :value="3"
                    >
                    {{ $t('contracts.location_sharing_mode_all') }}
                </label>
            </div>
        </div>
        <contract-task-types
            ref="taskTypeSelector"
            v-model="contract.task_types"
            class="my-3"
            :use-default="contract.id<1"
        />
        <button
            v-if="!contractTripList"
            class="btn btn-success form-button mb-3"
            @click.stop="$emit('addTaskType')"
        >
            {{ $t('contracts.add_tasktype') }}
        </button>
        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="elyVisible = !elyVisible"
                >
                    {{ $t('contracts.ely_info') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="elyVisible = !elyVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:elyVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: elyVisible }"
        >
            <span class="tip-light pb-3">{{ $t('contracts.ely_hint') }}</span>
            <div class="row">
                <div class="col-sm-6">
                    <div>
                        <input
                            id="elyContract"
                            v-model="contract.ely_contract"
                            type="checkbox"
                            style="margin-right: .75em"
                            class="form-check-input"
                        >
                        <label
                            class="form-check-label"
                            for="elyContract"
                        >
                            {{ $t('contracts.ely_contract') }}
                        </label>
                    </div>
                </div>
                <div class="col-sm-6">
                    <input
                        id="diaryAutomation"
                        v-model="contract.diary_automation"
                        type="checkbox"
                        style="margin-right: .75em"
                        class="form-check-input"
                    >
                    <label
                        class="form-check-label"
                        for="diaryAutomation"
                    >
                        {{ $t('contracts.diary_automation') }}
                    </label>
                    <span class="tip-light">{{ $t('contracts.diary_automation_hint') }}</span>
                </div>
            </div>
            <div class="row pb-4">
                <div class="col-sm-6">
                    <span class="span-title">
                        {{ $t('contracts.velho_contract_id') }}
                    </span>
                    <div>
                        <select
                            id="externalContracts"
                            v-model="externalContract"
                            class="form-select form-select-sm"
                        >
                            <option :value="null">
                                {{ $t('contracts.external.select_external_contract') }}
                            </option>
                            <option
                                v-for="c in externalContracts"
                                :key="c.id"
                                :value="c.id"
                            >
                                {{ c.external_name + ' - ' + c.external_id }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-6">
                    <span class="span-title">
                        {{ $t('contracts.harja_contract_id') }}
                    </span>
                    <input
                        id="harja_contract_id"
                        v-model="contract.harja_contract_id"
                        type="number"
                        step="1"
                        class="form-control form-control-sm"
                    >
                </div>
            </div>
        </div>
        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="louhiVisible = !louhiVisible"
                >
                    {{ $t('contracts.louhi_info') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="louhiVisible = !louhiVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:louhiVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: louhiVisible }"
        >
            <span class="tip-light">{{ $t('contracts.louhi_hint') }}</span>
            <div class="row nopads my-2">

                <div class="col-sm-4 nopads">
                    <span class="span-title">{{ $t('contracts.louhi_select_contract') }}</span>
                    <div class="d-flex gap-2 align-items-center">
                        <select
                            v-model="selectedLouhiContract"
                            class="form-select form-select-sm"
                            :disabled="louhiContracts.length < 1"
                        >
                            <option :value="null">{{ $t('contracts.louhi_no_contract') }}</option>
                            <option
                                v-for="louhiContract in louhiContracts"
                                :key="louhiContract.yksilointitieto"
                                :value="louhiContract.yksilointitieto"
                            >
                                {{ louhiContract.nimi }}
                            </option>
                        </select>
                        <button
                            class="btn btn-sm btn-outline-success"
                            @click="createNewLouhiContract"
                            :disabled="!contract.name || !contract.name.trim()"
                        >
                            <font-awesome-icon icon="plus" />
                        </button>
                        <small class="text-muted" style="font-size: 0.75em;">{{ $t('contracts.louhi_create_new_contract') }}</small>
                    </div>
                </div>

                <div class="col-sm-4 nopads">
                    <span class="span-title">{{ $t('contracts.louhi_contract_name') }}</span>
                    <input
                        :value="louhiContractName"
                        readonly
                        class="form-control form-control-sm"
                        placeholder=""
                    />
                </div>

                <div class="col-sm-4 nopads">
                    <span class="span-title">{{ $t('contracts.louhi_contract_uuid') }}</span>
                    <input
                        id="louhiUrakkaUUID"
                        :value="contract.louhi_id || ''"
                        readonly
                        class="form-control form-control-sm"
                        placeholder=""
                    />
                </div>
            </div>
            <div class="row nopads mt-1" v-if="louhiStatusText">
                <div class="col-12">
                    <small class="text-info">{{ louhiStatusText }}</small>
                </div>
            </div>
        </div>

        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="areasVisible = !areasVisible"
                >
                    {{ $t('contracts.areas_and_reporting') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="areasVisible = !areasVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:areasVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: areasVisible }"
        >
            <div class="row">
                <div class="col-sm-4 nopads">
                    <!-- Select area group-->
                    <span class="span-title">{{ $t('contracts.area_group') }}</span>
                    <div>
                        <select
                            id="areaGroups"
                            v-model="contract.area_group.id"
                            class="form-select form-select-sm sm-3"
                            :disabled="areaGroups.length < 1"
                            @change="checkAreaTracking"
                        >
                            <option :value="null">
                                {{ $t('contracts.select_area_group') }}
                            </option>
                            <option
                                v-for="option in areaGroups"
                                :key="option.id"
                                :value="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-sm-4 nopads px-2">
                    <!-- Area work record tracking option-->
                    <span class="span-title">{{ $t('contracts.area_tracking') }}</span>
                    <div>
                        <input
                            id="trackAreaWork"
                            v-model="contract.track_area_work"
                            type="checkbox"
                            class="form-check-input pt-2"
                            style="margin-top: .5em; margin-right: .5em"
                            :disabled="!contract.area_group || !contract.area_group.id || contract.area_group.id < 1"
                        >
                        <label
                            class="form-check-label"
                            for="trackAreaWork"
                            style="margin-top: .25em"
                        >
                            {{ $t('contracts.area_tracking_enabled') }}
                        </label>
                    </div>
                </div>
                <div class="col-sm-4 nopads px-2">
                    <!-- Area trip tracking mode option-->
                    <span class="span-title">{{ $t('contracts.area_tracking_mode') }}</span>
                    <select
                        v-model="contract.report_tracking_mode"
                        class="form-select form-select-sm"
                        :disabled="!contract.area_group || !contract.area_group.id || contract.area_group.id < 1"
                    >
                        <option
                            v-for="option in trackModeOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.text }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="weatherVisible = !weatherVisible"
                >
                    {{ $t('contracts.weather_tracking.title') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="weatherVisible = !weatherVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:weatherVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: weatherVisible }"
        >
            <div class="row">
                <div class="col-sm-4">
                    <select
                        v-model="weatherTrackingMode"
                        class="form-select form-select-sm col-sm-2"
                    >
                        <option
                            v-for="option in weatherTrackingModeOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.text }}
                        </option>
                    </select>
                </div>
                <div class="col-sm-4">
                    <button
                        v-if="weatherTrackingMode === weatherTrackingModes.POINT"
                        id="submit"
                        class="btn btn-primary ml-4"
                        :disabled="weatherTrackingMode !== weatherTrackingModes.POINT"
                        @click.stop="editWeatherTracking = true"
                    >
                        {{ $t('contracts.weather_tracking.edit_position') }}
                    </button>
                </div>
                <coordinate-editor-modal
                    v-if="updatedLocation && editWeatherTracking"
                    v-model="updatedLocation.position"
                    :show-weather-stations="true"
                    :title="$t('contracts.weather_tracking.edit').toString()"
                    :visible="editWeatherTracking"
                    @close="editWeatherTracking = false"
                    @setLocation="updateLocation"
                />
            </div>
        </div>
        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="inspectionsVisible = !inspectionsVisible"
                >
                    {{ $t('contracts.inspections_and_certificates') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="inspectionsVisible = !inspectionsVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:inspectionsVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: inspectionsVisible }"
        >
            <contract-inspections
                v-model="contract.inspections"
                :use-default="contract.id<1"
                class="my-3"
            />
            <contract-user-inspections
                v-model="contract.user_inspections"
                :use-default="contract.id<1"
                class="my-3"
            />
            <contract-mvr-inspection-templates
                v-model="contract.mvr_inspections"
                class="my-3"
            />
            <contract-certificate-types
                v-model="contract.certificate_types"
                class="my-3"
            />
        </div>
        <hr class="mb-1 mt-1">
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="storagesVisible = !storagesVisible"
                >
                    {{ $t('contracts.share_to_material_storage') }}
                </h4>
            </div>
            <div
                class="ms-2 px-2"
                @click.stop="storagesVisible = !storagesVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated:storagesVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: storagesVisible }"
        >
            <span class="tip-light"> {{ $t('contracts.share_hint') }}</span>
            <div
                class="col-12 nopads"
                style="min-height: 15em"
            >
                <contract-material-storages
                    v-model="contract.material_storages"
                    class="pb-4"
                    :contract="contract"
                />
            </div>
        </div>
        <hr class="mb-1 mt-1">
        <div class="col-sm-12 d-flex justify-content-end pb-3 pt-4 mt-5">
            <button
                v-if="contract.id > 0"
                class="btn btn-info form-button"
                @click.stop="showContractTripModal = !showContractTripModal"
            >
                {{ $t('daily_summary.generate_report') }}
            </button>
            <contract-copy
                v-if="!contractTripList && contract.id > 0"
                :contract="contract"
                :set-default-values="setDefaultValues"
                @contract-copied="softUpdateContract"
            />
            <button
                class="btn btn-secondary form-button"
                @click.stop="$emit('close')"
            >
                {{ contractTripList ? $t('common.close') : $t('contracts.close_form') }}
            </button>
            <button
                class="btn btn-success form-button"
                :disabled="loading"
                @click.stop="submitContract"
            >
                <span v-if="contract.id > 0">
                    {{ $t('contracts.save_contract') }}
                </span>
                <span v-else>
                    {{ $t('contracts.save_add_contract') }}
                </span>
            </button>
        </div>
        <base-modal
            v-if="showContractTripModal"
            ref="contractTripReportModal"
            hide-footer
            :title="$t('daily_summary.generation_title')"
            @close="showContractTripModal = false"
        >
            <ContractTripSummaryGenerator
                :one-day-mode="true"
                :contract-object="contract"
                @close-modal="showContractTripModal = false"
            />
        </base-modal>
        <base-modal
            v-if="showFetchImportedDataModal"
            size="lg"
            hide-footer
            hide-header-close
            :title="$t('contracts.fetch_imported_contract_data')"
        >
            <contract-data-importer
                :contract-object="contract"
                @close-modal="showFetchImportedDataModal = false"
                @data-updated="successContractDataImporterModal"
            />
        </base-modal>
        <base-modal
            v-if="showUpdatePriceModal"
            hide-header
            hide-footer
            size="lg"
            @close="showUpdatePriceModal = false"
        >
            <contract-data-price-updater
                :contract-object="contract"
                @close-modal="showUpdatePriceModal = false"
                @close-success-modal="successContractDataUpdaterModal"
            />
        </base-modal>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import ContractInspections from "./ContractInspections";
import ContractMvrInspectionTemplates from "./ContractMvrInspectionTemplates";
import ContractTaskTypes from "./ContractTaskTypes";
import ContractCertificateTypes from "./ContractCertificateTypes";
import ContractMaterialStorages from "./ContractMaterialStorages";
import ContractTripSummaryGenerator from "@/components/tripsummary/ContractTripSummaryGenerator";
import ContractCopy from "./ContractCopy";
import ContractDataImporter from "@/components/contract/contractdataimporter/ContractDataImporter";
import {userHelper} from "@/components/mixins/UserMixin";
import {mapHelper} from "../mixins/MapMixin";
import CoordinateEditorModal from "../CoordinateEditorModal";
import ContractDataPriceUpdater from "@/components/contract/contractdataimporter/ContractDataPriceUpdater.vue";
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";
import ContractUserInspections from "./ContractUserInspections.vue";

export default {
    name: 'ContractEditor',
    components: {
        ContractUserInspections,
        BaseModal,
        ContractDataPriceUpdater,
        CoordinateEditorModal,
        ContractDataImporter, ContractInspections, ContractMvrInspectionTemplates, ContractTaskTypes, ContractCertificateTypes, ContractCopy,
        ContractTripSummaryGenerator, ContractMaterialStorages},
    mixins: [workManagementHelper, restApi, userHelper, mapHelper],
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        contractTripList: {
            type: Boolean,
            default: false
        },
    },
    emits: ['update:modelValue', 'addTaskType'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            contract: null,
            externalContract: null,
            showContractTripModal: false,
            showFetchImportedDataModal: false,
            showUpdatePriceModal: false,
            taskTypes: [],
            areaGroups: [],
            externalContracts: [],
            contractCollectionOptions: [],
            contractClassOptions: [],
            costCenters: [],
            contractClass: null,
            contractClassEditableOverride: false,
            contractCollection: null,
            users: [],
            disabledUsers: [],
            allowedUsers: [],
            trackModeOptions: [
                { value: 1, text: this.$t('contracts.area_tracking_mode_1') },
                //{ value: 2, text: this.$t('contracts.area_tracking_mode_2') },
                { value: 3, text: this.$t('contracts.area_tracking_mode_3') }
            ],
            weatherTrackingMode: 0,
            weatherTrackingModes: {
                NONE: 0,
                POINT: 1,
                AREAS: 2,
            },
            editWeatherTracking: false,
            elyVisible: false,
            louhiVisible: false,
            weatherVisible: false,
            inspectionsVisible: false,
            areasVisible: false,
            storagesVisible: false,
            louhiUrakkaUUID: null,
            louhiContracts: [],           // Array of available Louhi contracts
            selectedLouhiContract: null,   // Currently selected contract from dropdown,
            updatedLocation: null
        }
    },
    computed: {
        isInvestment() {
            return this.contractClass ? this.contractClass.type === 2 : false
        },
        isContractClassLocked() {
            return this.contract.id > 0
                && !!this.contractClass
                && !this.contractClassEditableOverride
        },
        weatherTrackingModeOptions() {
            return [
                { value: this.weatherTrackingModes.NONE, text: this.$t('contracts.weather_tracking.mode.none') },
                { value: this.weatherTrackingModes.POINT, text: this.$t('contracts.weather_tracking.mode.point') },
                { value: this.weatherTrackingModes.AREAS, text: this.$t('contracts.weather_tracking.mode.areas'), disabled: this.contract.area_group.id == null },
            ]
        },
        louhiContractName() {
            if (!this.contract.louhi_id || !this.louhiContracts.length) return ''
            const matchingContract = this.louhiContracts.find(lc => lc.yksilointitieto === this.contract.louhi_id)
            return matchingContract ? matchingContract.nimi : ''
        },
        louhiStatusText() {
            const hasCurrentContract = !!this.contract.louhi_id
            const hasSelectedContract = !!this.selectedLouhiContract

            if (!hasSelectedContract && !hasCurrentContract) {
                return this.$t("contracts.louhi_no_changes")
            }

            if (!hasSelectedContract && hasCurrentContract) {
                return this.$t("contracts.louhi_will_remove_binding")
            }

            if (hasSelectedContract && !hasCurrentContract) {
                const selectedContract = this.louhiContracts.find(lc => lc.yksilointitieto === this.selectedLouhiContract)
                return this.$t("contracts.louhi_will_bind_to") + ": " + (selectedContract?.nimi || "")
            }

            if (hasSelectedContract && hasCurrentContract && this.selectedLouhiContract !== this.contract.louhi_id) {
                const selectedContract = this.louhiContracts.find(lc => lc.yksilointitieto === this.selectedLouhiContract)
                return this.$t("contracts.louhi_will_switch_to") + ": " + (selectedContract?.nimi || "")
            }

            return null
        }
    },
    watch: {
        modelValue() {
            this.contract = this.modelValue
        },
        weatherTrackingMode(value) {
            this.modelValue.weather_tracking_mode = value
            this.setPointWeatherTrackingState(value === this.weatherTrackingModes.POINT)
        },
        contractClass() {
            if (!this.contractClass || this.contractClass.type === 1) {
                this.contract.code = null
            }
            if (!this.contractClass || this.contractClass.type === 2) {
                this.contract.cost_center = null
            }
        }
    },
    created() {
        this.modelValue.start_date = new Date(this.modelValue.start_date).getTime()
        this.modelValue.end_date = new Date(this.modelValue.end_date).getTime()
        if (this.modelValue.weather_tracking_mode != null) {
            this.weatherTrackingMode = this.modelValue.weather_tracking_mode
        } else if (this.modelValue.weather_tracking_position != null) {
            this.weatherTrackingMode = this.weatherTrackingModes.POINT
            this.updatedLocation = JSON.parse(JSON.stringify(this.modelValue.weather_tracking_position));
        } else {
            this.weatherTrackingMode = this.weatherTrackingModes.NONE
        }
        this.contract = this.modelValue
        this.externalContract = this.contract.external_contract ? this.contract.external_contract.id : null
        this.fetchAreaGroups()
        this.fetchUsers()
        this.fetchExternalContracts()
        this.fetchContractCollections()
        this.fetchContractClasses()
        this.fetchCostCenters()
        this.fetchLouhiContracts()
        this.contractCollection = this.contract.contract_collection ? this.contract.contract_collection.id : null
        this.contractClass = this.contract.contract_class ? this.contract.contract_class : null
        this.contractClassEditableOverride = !(this.contract.id > 0 && !!this.contractClass)
    },
    methods: {
        fetchContractCollections: function () {
            this.loading = true
            this.contractCollectionOptions = []
            this.restFetch(this.contractCollectionUrl, this.handleResponse)
        },
        handleResponse: function (response) {
            this.contractCollectionOptions = response.data
            // Check if contract's collection is included and if not
            if (this.contract.contract_collection) {
                let exists = false
                this.contractClassOptions.forEach(c => {
                    if (c.id === this.contract.contract_collection.id) {
                        exists = true
                    }
                })
                if (!exists) {
                    // Add contract's collection to options
                    this.contractCollectionOptions.push(this.contract.contract_collection)
                }
            }
            this.loading = false
        },
        fetchContractClasses: function () {
            this.loading = true
            this.contractClassOptions = []
            this.restFetchParams(this.contractClassUrl, {in_use: 1}, this.handleContractClassResponse)
        },
        handleContractClassResponse: function (response) {
            this.contractClassOptions = response && response.data ? response.data : []
            this.loading = false
        },
        fetchCostCenters: function () {
            this.loading = true
            this.costCenters = []
            this.restFetch(this.costCenterUrl, this.handleCostCenterResponse)
        },
        getCostCenterString(costCenter) {
            return costCenter.number + ' - ' + costCenter.name
        },
        handleCostCenterResponse: function (response) {
            this.costCenters = response && response.data ? response.data : []
            this.loading = false
        },
        setPointWeatherTrackingState(active) {
            if (!active && !this.modelValue.weather_tracking_position) return;
            this.modelValue.weather_tracking_position = {
                position: {
                    x: this.user.company.location.x,
                    y: this.user.company.location.y
                },
                ...this.modelValue.weather_tracking_position,
                active,
            }
            this.updatedLocation = JSON.parse(JSON.stringify(this.modelValue.weather_tracking_position));
        },
        softUpdateContract(data) {
            if (!data.area_group) {
                data.area_group = {id: null}
            }
            this.contract.id = data.id
            this.$emit('update:modelValue', data)
            this.updateContractMaterialStorages()
        },
        checkAreaTracking: function () {
            if (!this.contract.area_group || !this.contract.area_group.id || this.contract.area_group < 1) {
                this.contract.track_area_work = false
                if (this.weatherTrackingMode === this.weatherTrackingModes.AREAS) {
                    this.weatherTrackingMode = this.weatherTrackingModes.NONE
                    this.showAlert(this.$t('contracts.weather_tracking.info.turned_off_from_area'))
                }
            }
        },
        fetchAreaGroups: function () {
            this.areaGroups = []
            this.loading = true
            this.restFetchParams(this.areaGroupUrl, {
                company: this.contract.customer.id,
                no_geom: 1
            }, this.handleAreaGroupResponse)
        },
        handleAreaGroupResponse: function (response) {
            this.loading = false
            if (response != null) {
                let groups = response.data
                if (!!this.user && !this.hasAdmin(this.user.roles)) {
                    groups = response.data.filter(group => group.company_area === this.user.company.id)
                }
                this.areaGroups = groups
            }
        },
        fetchExternalContracts: function () {
            this.externalContracts = []
            this.loading = true
            this.restFetch(this.extContractIdCompanyUrl, this.handleExtContractResponse)
        },
        handleExtContractResponse: function (response) {
            this.loading = false
            if (response != null) {
                this.externalContracts = response.data
            }
        },
        fetchUsers: function () {
            this.loading = true;
            this.users = []
            this.disabledUsers = []
            this.allowedUsers = []
            this.restFetchParams(this.userCompanyUrl, {active: 1}, this.handleUserResponse)
        },

        handleUserResponse: function (response) {
            if (response && response.data) {
                response.data.forEach(function (user) {
                    this.users.push({
                        last_name: user.last_name,
                        first_name: user.first_name,
                        id: user.id
                    })
                }, this)
                this.handleOwners()
            }
            this.loading = false
        },

        handleOwners() {
            if (this.contract && this.contract.owners && this.contract.owners.length > 0) {
                this.contract.owners.forEach(owner => {
                    if (this.users.some(user => user.id === owner.id)) {
                        // If the owner is found in the users array, add to allowedUsers
                        this.allowedUsers.push(owner);
                    } else {
                        // If not found, add to disabledUsers
                        this.disabledUsers.push(owner);
                    }
                });
            }
        },

        updateStartDate: function (event) {
            if (event.target.value) {
                this.contract.start_date = new Date(event.target.value)
            } else {
                this.contract.start_date = undefined
            }
        },

        updateEndDate: function (event) {
            if (event.target.value) {
                this.contract.end_date = new Date(event.target.value)
            } else {
                this.contract.end_date = undefined
            }
        },

        setDefaultValues: function (value) {
            let contract = JSON.parse(JSON.stringify(value))
            if (contract.name === '') {
                this.showAlert(this.$t('contracts.invalid_name'))
                return undefined
            }
            if (contract.customer < 1) {
                this.showAlert(this.$t('contracts.invalid_customer'))
                return undefined
            }
            if (contract.contractor < 1) {
                this.showAlert(this.$t('contracts.invalid_contractor'))
                return undefined
            }
            if (contract.work_site_key === '') {
                contract.work_site_key = ''
            }
            if (contract.contract_number === '') {
                contract.contract_number = ''
            }
            if (contract.start_date && (contract.start_date instanceof Date)) {
                contract.start_date = contract.start_date.toISOString()
            }
            if (contract.end_date && (contract.end_date instanceof Date)) {
                contract.end_date = contract.end_date.toISOString()
            }
            if (contract.area_group.id) {
                contract.area_group = {id: contract.area_group.id}
            } else {
                contract.area_group = null
            }
            if (this.externalContract) {
                contract.external_contract = {id: this.externalContract}
            } else {
                contract.external_contract = null
            }
            if(this.contractCollection) {
                contract.contract_collection = {id: this.contractCollection}
            } else {
                contract.contract_collection = null
            }
            if(this.contractClass) {
                contract.contract_class = this.contractClass
            } else {
                contract.contract_class = null
            }
            contract.owners = [...this.disabledUsers, ...this.allowedUsers]
            // Add Louhi contract handling
            if (this.selectedLouhiContract) {
                contract.louhi_contract = this.selectedLouhiContract
            } else {
                contract.louhi_contract = null
            }
            return contract
        },

        submitContract: function () {
            let jsonContract = this.setDefaultValues(this.contract)
            if (jsonContract !== undefined) {
                this.loading = true
                if (this.contract.id < 1) {
                    this.restAdd(this.contractUrl, jsonContract, this.success, this.fail)
                } else {
                    this.restUpdate(this.contractUrl, jsonContract, this.success, this.failContractSubmit)
                }
            }
        },

        success: function (data) {
            if (!data.area_groups) {
                data.area_group = {id: null}
            }
            this.contract.task_types = data.task_types
            if (!this.contract.id || this.contract.id < 1) {
                this.contract.id = data.id
            }
            this.contractClassEditableOverride = !(this.contract.id > 0 && !!this.contractClass)
            this.$emit('update:modelValue', data)
            this.updateContractMaterialStorages()
            this.fetchUsers()
            this.loading = false
            this.updateContract()
        },

        isContractClassLockRejection(response) {
            return !!response
                && response.status === 400
                && typeof response.data === 'string'
                && response.data.includes('Contract class cannot be changed once it has been set')
        },

        failContractSubmit(response) {
            if (this.isContractClassLockRejection(response)) {
                this.loading = false
                this.showAlert(this.$t('contracts.contract_class_locked_error'))
                return
            }
            this.fail(response)
        },

        fail() {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },

        updateContractMaterialStorages: function () {
            if (this.contract.material_storages) {
                let materialStorageIds = []
                this.contract.material_storages.forEach(materialStorage=> {
                    materialStorageIds.push(materialStorage.id)
                })
                this.loading = true
                this.restUpdate(this.materialStorageUrl + '/contract/' + this.contract.id, {storages: materialStorageIds}, this.successContractMaterialStorages, this.fail)
            } else {
                this.updateContract()
            }
        },

        successContractDataImporterModal() {
            this.showFetchImportedDataModal = false
            this.updateContract()
        },

        successContractDataUpdaterModal() {
            this.showUpdatePriceModal = false
            this.updateContract()
        },

        successContractMaterialStorages: function(data) {
            this.contract.material_storage = data
            this.updateContract()
            this.loading = false
        },

        updateContract: function () {
            let jsonContract = this.setDefaultValues(this.contract)
            if (jsonContract !== undefined && this.contract.id > 0) {
                this.loading = true
                this.restFetchParams(this.contractUrl, {id: this.contract.id}, this.successUpdate, this.fail)
            }
        },

        successUpdate: function (response) {
            if (response.data && response.data.length > 0) {
                let contractData = response.data[0]
                if (!contractData.area_group) {
                    contractData.area_group = {id: null}
                }
                this.$emit('update:modelValue', contractData)
            }
            this.loading = false
        },

        refreshTaskTypes() {
            this.$refs.taskTypeSelector.fetchTaskTypes()
        },

        fetchLouhiContracts: function () {
            this.louhiContracts = []
            this.loading = true
            this.restFetch(this.louhiContractUrl, this.handleLouhiContractsResponse)
        },

        handleLouhiContractsResponse: function (response) {

            this.loading = false
            if (response != null) {
                this.louhiContracts = response.data

                this.$nextTick(() => {
                    const matchingContract = this.louhiContracts.find(lc => lc.yksilointitieto === this.contract.louhi_id)
                    this.selectedLouhiContract = matchingContract ? matchingContract.yksilointitieto : null
                })
            }
        },

        createNewLouhiContract() {
            if (!this.contract.name) {
                this.showAlert(this.$t('contracts.contract_name_required'))
                return
            }

            this.loading = true
            // Call API to create new Louhi contract
            this.restAdd(this.louhiContractUrl + '/' + this.contract.id, {
                contract_name: this.contract.name
            }, this.handleNewLouhiContractSuccess, this.handleNewLouhiContractError)
        },

        handleNewLouhiContractSuccess(response) {
            this.loading = false
            this.contract.louhi_id = response.yksilointitieto
            this.selectedLouhiContract = response.yksilointitieto
            this.fetchLouhiContracts()
            this.showAlert(this.$t('contracts.louhi_contract_created_successfully'), 'success')
        },

        handleNewLouhiContractError() {
            this.loading = false
            this.showAlert(this.$t('contracts.louhi_contract_creation_failed'))
        },

        updateLocation() {
            this.contract.weather_tracking_position = this.updatedLocation
            this.editWeatherTracking = false
        }
    }
}
</script>
